import { IkeId } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { T, Translate } from '../../i18n/index';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { MAX_UPLOAD_BATCH_SIZE, UploadBatch, type UploadBatchItem } from '../../services/field.upload/upload.batch';
import { MAX_UPLOAD_FILE_SIZE_MB, type UploadValidationRules } from '../../util/upload.reader';
import { Icon } from '../icon';
import { MaterialIcon } from '../icon/icons';
import { IMithrilStateComponent } from '../mithril.component';
import { ModalService } from '../modal/modal.service';
import { Spinner } from '../spinner/spinner';
import { addStyles, DefaultTheme } from '../theme';

export const CSS = {
	container: 'c-UploadModal',
	dropZone: 'c-UploadModal__drop-zone',
	dropZoneCompact: 'c-UploadModal__drop-zone--compact',
	dropZoneActive: 'c-UploadModal__drop-zone--is-active',
	dropZoneIcon: 'c-UploadModal__drop-zone-icon',
	dropZoneHint: 'c-UploadModal__drop-zone-hint',
	browseButton: 'c-UploadModal__browse',
	file: 'c-UploadModal__file',
	header: 'c-UploadModal__header',
	headerCount: 'c-UploadModal__header-count',
	grid: 'c-UploadModal__grid',
	tile: 'c-UploadModal__tile',
	tileExcluded: 'c-UploadModal__tile--excluded',
	tileImage: 'c-UploadModal__tile-image',
	tilePlaceholder: 'c-UploadModal__tile-placeholder',
	tileOverlay: 'c-UploadModal__tile-overlay',
	tileRemove: 'c-UploadModal__tile-remove',
	tileName: 'c-UploadModal__tile-name',
	tileError: 'c-UploadModal__tile-error',
	excludedNote: 'c-UploadModal__excluded-note',
	progress: 'c-UploadModal__progress',
	summary: 'c-UploadModal__summary',
	summaryOk: 'c-UploadModal__summary--ok',
};

const TILE_SIZE = '92px';

addStyles({
	[CSS.container]: {
		display: 'flex',
		'flex-direction': 'column',
		gap: DefaultTheme.space(3),
		// The modal sizes to its content, so pin the width or a wide tile grid stretches the dialog.
		width: '520px',
		'max-width': '80vw',
	},
	[CSS.dropZone]: {
		display: 'flex',
		'flex-direction': 'column',
		'justify-content': 'center',
		'align-items': 'center',
		gap: DefaultTheme.space(2),
		padding: DefaultTheme.space(6),
		'min-height': '180px',
		border: '2px dashed ' + DefaultTheme.font.color.disabled,
		'border-radius': '4px',
		color: DefaultTheme.font.color.supporting,
		'text-align': 'center',
		cursor: 'pointer',
		'&:hover': {
			'border-color': DefaultTheme.color.accent,
		},
	},
	[CSS.dropZoneCompact]: {
		'flex-direction': 'row',
		padding: DefaultTheme.space(2),
		'min-height': 'auto',
		gap: DefaultTheme.space(2),
	},
	[CSS.dropZoneActive]: {
		'border-color': DefaultTheme.color.accent,
		'background-color': DefaultTheme.background.lightGrey,
	},
	[CSS.dropZoneIcon]: {
		color: DefaultTheme.font.color.disabled,
	},
	[CSS.dropZoneHint]: {
		'font-size': '12px',
		color: DefaultTheme.font.color.disabled,
	},
	[CSS.browseButton]: {
		'margin-top': DefaultTheme.space(1),
	},
	[CSS.file]: {
		display: 'none',
	},
	[CSS.header]: {
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'space-between',
	},
	[CSS.headerCount]: {
		'font-weight': DefaultTheme.font.weight.bold,
	},
	[CSS.grid]: {
		display: 'flex',
		'flex-wrap': 'wrap',
		gap: DefaultTheme.space(2),
		'max-height': '280px',
		'overflow-y': 'auto',
	},
	[CSS.tile]: {
		position: 'relative',
		width: TILE_SIZE,
		display: 'flex',
		'flex-direction': 'column',
		gap: DefaultTheme.space(1),
	},
	[CSS.tileExcluded]: {
		opacity: 0.55,
	},
	[CSS.tileImage]: {
		width: TILE_SIZE,
		height: TILE_SIZE,
		'object-fit': 'cover',
		'border-radius': '3px',
		'background-color': DefaultTheme.background.grey,
	},
	[CSS.tilePlaceholder]: {
		width: TILE_SIZE,
		height: TILE_SIZE,
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
		'border-radius': '3px',
		'background-color': DefaultTheme.background.grey,
		color: DefaultTheme.font.color.supporting,
	},
	[CSS.tileOverlay]: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: TILE_SIZE,
		height: TILE_SIZE,
		display: 'flex',
		'align-items': 'center',
		'justify-content': 'center',
		'border-radius': '3px',
		'background-color': 'rgba(0, 0, 0, 0.45)',
		color: DefaultTheme.font.color.white,
	},
	[CSS.tileRemove]: {
		position: 'absolute',
		top: '2px',
		right: '2px',
		width: DefaultTheme.space(5),
		height: DefaultTheme.space(5),
		'line-height': DefaultTheme.space(5),
		padding: 0,
		'min-width': 0,
		'border-radius': '50%',
		border: 'none',
		cursor: 'pointer',
		'background-color': 'rgba(0, 0, 0, 0.6)',
		color: DefaultTheme.font.color.white,
	},
	[CSS.tileName]: {
		'font-size': '11px',
		color: DefaultTheme.font.color.supporting,
		overflow: 'hidden',
		'text-overflow': 'ellipsis',
		'white-space': 'nowrap',
	},
	[CSS.tileError]: {
		'font-size': '11px',
		color: DefaultTheme.font.color.error,
	},
	[CSS.excludedNote]: {
		'font-size': '12px',
		color: DefaultTheme.font.color.error,
	},
	[CSS.progress]: {
		display: 'flex',
		'align-items': 'center',
		gap: DefaultTheme.space(2),
		'margin-right': 'auto',
	},
	[CSS.summary]: {
		'margin-right': 'auto',
		color: DefaultTheme.font.color.error,
	},
	[CSS.summaryOk]: {
		color: DefaultTheme.font.color.supporting,
	},
});

export interface UploadModalOptions {
	title: T;
	/** Omit when any type is allowed. */
	accept?: string;
	rules: UploadValidationRules;
	/** Rejecting marks only that file failed. */
	upload: (item: UploadBatchItem) => Promise<void>;
	/** Defaults to true. */
	multiple?: boolean;
}

/**
 * Nothing is sent until the user confirms: an attached photo cannot be removed from a field
 * afterwards (only the whole field can be cleared), so the review step is what makes an accidental
 * folder-drop recoverable.
 */
export class UploadModal extends IMithrilStateComponent {
	private readonly options: UploadModalOptions;
	private readonly inputId: string;

	@observable.ref private batch: UploadBatch;
	@observable private isDraggingOver: boolean = false;

	constructor(options: UploadModalOptions) {
		super();

		this.options = options;
		this.inputId = 'upload-modal-file-' + IkeId.generate();
		this.batch = this.createBatch();

		// After the assignments: with `useDefineForClassFields: false` an un-initialized field does
		// not exist yet for `makeObservable` to see, and `batch` cannot take a declaration default.
		makeObservable(this);
	}

	private createBatch(): UploadBatch {
		return new UploadBatch(this.options.rules, this.options.multiple === false ? 1 : MAX_UPLOAD_BATCH_SIZE);
	}

	async open(): Promise<void> {
		try {
			await ModalService.open({
				title: this.options.title,
				content: () => this.view(),
				actions: () => this.viewActions(),
				canClose: () => this.canClose,
			});
		} finally {
			this.batch.releasePreviews();
		}
	}

	@computed private get canClose(): boolean {
		return !this.batch.isRunning;
	}

	@computed private get isEmpty(): boolean {
		return this.batch.total === 0;
	}

	get nodes(): MithrilVNode {
		return m('div', { className: CSS.container, 'data-testid': 'upload-modal' }, [
			this.isEmpty ? null : this.viewHeader(),
			this.isEmpty ? null : this.viewGrid(),
			this.viewExcludedNote(),
			this.viewDropZone(),
		]);
	}

	private viewHeader(): MithrilVNode {
		const batch = this.batch;
		const label = batch.hasStarted
			? Translate(T.UPLOAD_MODAL_ADDED_COUNT, { count: batch.successCount })
			: Translate(T.UPLOAD_MODAL_SELECTED_COUNT, { count: batch.countingTowardLimit });

		return m('div', { className: CSS.header }, [
			m('span', { className: CSS.headerCount, 'data-testid': 'upload-modal-count' }, label),
		]);
	}

	private viewGrid(): MithrilVNode {
		return m(
			'div',
			{ className: CSS.grid, 'data-testid': 'upload-modal-grid' },
			this.batch.items.map((item) => this.viewTile(item))
		);
	}

	private viewTile(item: UploadBatchItem): MithrilVNode {
		const className = CSS.tile + (item.isExcluded ? ' ' + CSS.tileExcluded : '');
		const previewUrl = item.previewUrl;

		return m('div', { className, 'data-testid': 'upload-modal-tile' }, [
			previewUrl != null
				? m('img', { className: CSS.tileImage, src: previewUrl, alt: item.name })
				: m('div', { className: CSS.tilePlaceholder }, Icon.view(MaterialIcon.FILE_UPLOAD)),
			this.viewTileOverlay(item),
			this.isRemovable ? this.viewRemoveButton(item) : null,
			m('div', { className: CSS.tileName, title: item.name }, item.name),
			item.error != null ? m('div', { className: CSS.tileError }, Translate(item.error.key, item.error.attrs)) : null,
		]);
	}

	private viewTileOverlay(item: UploadBatchItem): MithrilVNode {
		switch (item.status) {
			case 'uploading':
				return m('div', { className: CSS.tileOverlay }, Spinner.view());
			case 'done':
				return m('div', { className: CSS.tileOverlay }, Icon.view(MaterialIcon.CHECK));
			case 'failed':
			case 'invalid':
			case 'skipped':
				return m('div', { className: CSS.tileOverlay }, Icon.view(MaterialIcon.WARNING));
			default:
				return null;
		}
	}

	@computed private get isRemovable(): boolean {
		return !this.batch.hasStarted;
	}

	private viewRemoveButton(item: UploadBatchItem): MithrilVNode {
		return m(
			'button',
			{
				className: CSS.tileRemove,
				type: 'button',
				title: Translate(T.UPLOAD_MODAL_REMOVE),
				'data-testid': 'upload-modal-remove',
				onclick: (e: MouseEvent) => this.onRemoveFile(e, item),
			},
			'✕'
		);
	}

	private viewExcludedNote(): MithrilVNode {
		if (this.batch.excludedCount === 0) {
			return null;
		}

		return m(
			'div',
			{ className: CSS.excludedNote, 'data-testid': 'upload-modal-excluded' },
			Translate(T.UPLOAD_MODAL_EXCLUDED, { count: this.batch.excludedCount })
		);
	}

	private viewDropZone(): MithrilVNode {
		// Once sent, the modal is showing a result; adding more means opening a fresh one. Hiding it
		// at capacity too stops a single-file field offering "Add more" it would only reject.
		if (this.batch.hasStarted || !this.batch.hasCapacity) {
			return null;
		}

		let className = CSS.dropZone;
		if (!this.isEmpty) {
			className += ' ' + CSS.dropZoneCompact;
		}
		if (this.isDraggingOver) {
			className += ' ' + CSS.dropZoneActive;
		}

		return m('div', [
			m(
				'input',
				{
					className: CSS.file,
					type: 'file',
					id: this.inputId,
					'data-testid': 'upload-modal-input',
					accept: this.options.accept ?? undefined,
					multiple: this.options.multiple !== false,
					onchange: this.onInputChange,
				},
				''
			),
			m(
				'div',
				{
					className,
					'data-testid': 'upload-modal-drop-zone',
					onclick: this.openFilePicker,
					ondragover: this.onDragOver,
					ondragleave: this.onDragLeave,
					ondrop: this.onDrop,
				},
				this.isEmpty ? this.viewEmptyDropZone() : this.viewCompactDropZone()
			),
		]);
	}

	private viewEmptyDropZone(): MithrilVNode[] {
		return [
			Icon.view(MaterialIcon.FILE_UPLOAD, { className: CSS.dropZoneIcon }),
			m('div', Translate(this.options.multiple === false ? T.UPLOAD_MODAL_DROP_ONE : T.UPLOAD_MODAL_DROP_HERE)),
			m(
				'button',
				{
					className: 'mdl-button mdl-button--accent ' + CSS.browseButton,
					type: 'button',
					'data-testid': 'upload-modal-browse',
					onclick: this.openFilePicker,
				},
				Translate(T.UPLOAD_MODAL_BROWSE)
			),
			this.viewLimitsHint(),
		];
	}

	private viewCompactDropZone(): MithrilVNode[] {
		return [
			Icon.view(MaterialIcon.ADD, { className: CSS.dropZoneIcon }),
			m('span', Translate(T.UPLOAD_MODAL_ADD_MORE)),
			m('span', { className: CSS.dropZoneHint }, this.limitsHintText()),
		];
	}

	private viewLimitsHint(): MithrilVNode {
		return m('div', { className: CSS.dropZoneHint, 'data-testid': 'upload-modal-hint' }, this.limitsHintText());
	}

	private limitsHintText(): string {
		const parts: string[] = [];
		const displayTypes = this.options.rules.displayTypes;

		if (displayTypes != null && displayTypes.length > 0) {
			parts.push(Translate(T.UPLOAD_MODAL_ACCEPTED_TYPES, { types: displayTypes.join(', ') }));
		}

		parts.push(Translate(T.UPLOAD_MODAL_MAX_SIZE, { limit: `${MAX_UPLOAD_FILE_SIZE_MB} MB` }));

		if (this.options.multiple !== false) {
			parts.push(Translate(T.UPLOAD_MODAL_MAX_COUNT, { limit: MAX_UPLOAD_BATCH_SIZE }));
		}

		return parts.join(' · ');
	}

	private viewActions(): MithrilVNode {
		const batch = this.batch;
		const children: MithrilVNode[] = [];

		if (batch.isRunning) {
			children.push(
				m('div', { className: CSS.progress, 'data-testid': 'upload-modal-progress' }, [
					Spinner.view(),
					m('span', Translate(T.UPLOAD_BATCH_PROGRESS, { count: batch.settledCount, total: batch.uploadTotal })),
				])
			);
		} else if (batch.isComplete) {
			children.push(this.viewSummary());
		}

		children.push(
			m(
				'button',
				{
					className: 'mdl-button',
					type: 'button',
					disabled: !this.canClose,
					'data-testid': 'upload-modal-close',
					onclick: () => ModalService.close(batch.hasStarted),
				},
				Translate(batch.hasStarted ? T.DONE : T.CANCEL)
			)
		);

		if (batch.isComplete && batch.failureCount > 0) {
			children.push(
				m(
					'button',
					{
						className: 'mdl-button mdl-button--accent',
						type: 'button',
						'data-testid': 'upload-modal-retry',
						onclick: this.onRetry,
					},
					Translate(T.UPLOAD_MODAL_RETRY, { count: batch.failureCount })
				)
			);
		}

		if (!batch.hasStarted) {
			children.push(
				m(
					'button',
					{
						className: 'mdl-button mdl-button--accent',
						type: 'button',
						disabled: batch.readyCount === 0,
						'data-testid': 'upload-modal-upload',
						onclick: this.onUpload,
					},
					Translate(T.UPLOAD_MODAL_UPLOAD, { count: batch.readyCount })
				)
			);
		}

		return m.fragment({}, children);
	}

	private viewSummary(): MithrilVNode {
		const batch = this.batch;
		const notUploaded = batch.failureCount + batch.excludedCount;

		if (notUploaded === 0) {
			return m(
				'div',
				{ className: CSS.summary + ' ' + CSS.summaryOk, 'data-testid': 'upload-modal-summary' },
				Translate(T.UPLOAD_MODAL_ALL_DONE, { count: batch.successCount })
			);
		}

		return m(
			'div',
			{ className: CSS.summary, 'data-testid': 'upload-modal-summary' },
			Translate(T.UPLOAD_BATCH_SUMMARY, { successful: batch.successCount, failed: notUploaded })
		);
	}

	@action.bound private openFilePicker(e: Event) {
		e.preventDefault();
		e.stopPropagation();

		const input = Services.Window.document.getElementById(this.inputId);
		if (input == null) {
			return;
		}
		input.click();
	}

	@action.bound private onDragOver(e: DragEvent) {
		e.preventDefault();
		this.isDraggingOver = true;
	}

	@action.bound private onDragLeave(e: DragEvent) {
		e.preventDefault();
		this.isDraggingOver = false;
	}

	@action.bound private onDrop(e: DragEvent) {
		e.preventDefault();
		this.isDraggingOver = false;

		if (e.dataTransfer == null) {
			return;
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'batch:drop', 'drop', 1);
		this.addFiles(e.dataTransfer.files);
	}

	@action.bound private onInputChange(e: Event) {
		const input = e.target as HTMLInputElement;
		if (input == null || input.files == null) {
			return;
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'batch:click', 'button', 1);
		this.addFiles(input.files);
	}

	// Not `onRemove` — that is the Mithril lifecycle hook on the component base class.
	@action.bound private onRemoveFile(e: MouseEvent, item: UploadBatchItem) {
		e.preventDefault();
		e.stopPropagation();

		this.batch.remove(item);
	}

	@action.bound private onUpload(e: Event) {
		e.preventDefault();
		this.runUpload(() => this.batch.run(this.options.upload));
	}

	@action.bound private onRetry(e: Event) {
		e.preventDefault();
		this.runUpload(() => this.batch.retryFailed(this.options.upload));
	}

	/** Pre-loads a selection (e.g. a field dropped on directly). Nothing is sent until confirmed. */
	@action addFiles(files: FileList | File[]) {
		if (files.length === 0 || this.batch.hasStarted) {
			return;
		}

		this.batch.add(files);

		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Upload,
			'batch:count',
			String(this.batch.total),
			this.batch.total
		);
	}

	async start(): Promise<void> {
		await this.batch.run(this.options.upload);
	}

	/** DOM handlers cannot await. */
	private runUpload(run: () => Promise<void>) {
		run().catch((error) => {
			Services.Log.Logger.error({ error }, 'Upload batch failed unexpectedly');
		});
	}
}
